/**
 * Sync Order cart destination selector.
 * window.AonegndSyncCartSelector.open(options) => Promise<{type:'NEW_CART'}|{type:'EXISTING_CART',cartId:number}|null>
 */
(function (win) {
  'use strict';

  var FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** @typedef {{ id: number, uid: string|null, status: string, createdAt: string|null, expiresAt: string|null, totalItems: number, totalWeight: number|null, totalWeightUnit: string, totalPrice: string|null, currency: string|null, salesRepName: string|null }} NormalizedCart */
  /** @typedef {{ type: 'NEW_CART', cartLabel?: string } | { type: 'EXISTING_CART', cartId: number, cartLabel?: string }} SyncDestination */

  /** @type {{ modal: HTMLElement|null, resolve: ((value: SyncDestination|null) => void)|null, customer: {id:number,name:string,customerNumber:string}|null, selectedDestination: SyncDestination|null, activeCarts: NormalizedCart[], expiredCarts: NormalizedCart[], fetchState: 'idle'|'loading'|'error'|'ready', fetchSequence: number, abortController: AbortController|null, escHandler: ((e: KeyboardEvent) => void)|null, tabHandler: ((e: KeyboardEvent) => void)|null, lastFocus: Element|null, htmlOverflow: string, bodyOverflow: string, options: Record<string, unknown>|null, isSubmitting: boolean }} */
  var selectorState = {
    modal: null,
    resolve: null,
    customer: null,
    selectedDestination: null,
    activeCarts: [],
    expiredCarts: [],
    fetchState: 'idle',
    fetchSequence: 0,
    abortController: null,
    escHandler: null,
    tabHandler: null,
    lastFocus: null,
    htmlOverflow: '',
    bodyOverflow: '',
    options: null,
    isSubmitting: false,
  };

  /** @param {unknown} payload */
  function unwrapApiData(payload) {
    if (payload && typeof payload === 'object') {
      var root = /** @type {Record<string, unknown>} */ (payload);
      if (root.success === true && root.data && typeof root.data === 'object') {
        return root.data;
      }
      if (root.data && typeof root.data === 'object') {
        return root.data;
      }
    }
    return payload;
  }

  /** @param {string} path */
  function normalizeStorefrontPath(path) {
    var p = String(path || '').trim();
    if (!p) return '/apps/aone-api/carts';
    if (/^https?:\/\//i.test(p)) {
      try {
        var parsed = new URL(p);
        var pathname = parsed.pathname.replace(/\/+$/, '');
        return pathname || '/apps/aone-api/carts';
      } catch (_) {
        return '/apps/aone-api/carts';
      }
    }
    if (p.charAt(0) !== '/') p = '/' + p;
    return p.replace(/\/+$/, '');
  }

  /** @param {string} url @param {RequestInit} [opts] */
  async function fetchJson(url, opts) {
    var res = await fetch(url, {
      credentials: 'same-origin',
      ...(opts || {}),
      headers: {
        Accept: 'application/json',
        ...(opts && opts.headers ? opts.headers : {}),
      },
    });
    var txt = await res.text();
    /** @type {Record<string, unknown>} */
    var data = {};
    try {
      data = txt ? JSON.parse(txt) : {};
    } catch {
      data = {};
    }
    return { res, data };
  }

  /** @param {Element} container */
  function focusableList(container) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(nodes, function (el) {
      return (
        el instanceof HTMLElement &&
        !el.hasAttribute('hidden') &&
        (el.offsetParent !== null || (el.getClientRects && el.getClientRects().length > 0))
      );
    });
  }

  /** @param {unknown} value */
  function normalizeCartStatus(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  /** @param {unknown} raw */
  function pickFirst(raw, keys) {
    if (!raw || typeof raw !== 'object') return null;
    var obj = /** @type {Record<string, unknown>} */ (raw);
    for (var i = 0; i < keys.length; i++) {
      if (obj[keys[i]] != null && obj[keys[i]] !== '') return obj[keys[i]];
    }
    return null;
  }

  /** @param {unknown} raw @returns {number|null} */
  function toFiniteNumber(raw) {
    var n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  /** @param {unknown} raw @returns {NormalizedCart|null} */
  function normalizeCartRow(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var row = /** @type {Record<string, unknown>} */ (raw);
    var id = toFiniteNumber(row.id != null ? row.id : row.cart_id);
    if (id == null || !Number.isInteger(id) || id <= 0) return null;

    var uidRaw = pickFirst(row, ['uid', 'externalCartRef', 'external_cart_ref', 'cartUid', 'cart_uid']);
    var uid = uidRaw != null ? String(uidRaw).trim() : null;
    if (uid === '') uid = null;

    var status = normalizeCartStatus(row.status);
    var createdAtRaw = pickFirst(row, ['createdAt', 'created_at']);
    var expiresAtRaw = pickFirst(row, ['expiresAt', 'expires_at', 'expiredAt', 'expired_at']);
    var totalItems = toFiniteNumber(
      pickFirst(row, ['total_items', 'totalItems', 'itemsCount', 'items_count', 'totalQuantity', 'total_quantity'])
    );
    var totalWeight = toFiniteNumber(pickFirst(row, ['total_weight', 'totalWeight', 'totalWeightGrams', 'total_weight_grams']));
    var totalWeightUnitRaw = pickFirst(row, ['total_weight_unit', 'totalWeightUnit']);
    var totalPriceRaw = pickFirst(row, ['total_price', 'totalPrice', 'cartTotal', 'cart_total', 'total_amount', 'totalAmount']);
    var currencyRaw = pickFirst(row, ['currency', 'currencyCode', 'currency_code']);
    var salesRepNameRaw = pickFirst(row, ['salesRepName', 'sales_rep_name', '_sales_rep_name']);

    return {
      id: id,
      uid: uid,
      status: status,
      createdAt: createdAtRaw != null ? String(createdAtRaw) : null,
      expiresAt: expiresAtRaw != null ? String(expiresAtRaw) : null,
      totalItems: totalItems != null ? Math.max(0, Math.trunc(totalItems)) : 0,
      totalWeight: totalWeight,
      totalWeightUnit: totalWeightUnitRaw != null ? String(totalWeightUnitRaw) : 'g',
      totalPrice: totalPriceRaw != null ? String(totalPriceRaw) : null,
      currency: currencyRaw != null ? String(currencyRaw) : null,
      salesRepName: salesRepNameRaw != null ? String(salesRepNameRaw).trim() : null,
    };
  }

  /** @param {NormalizedCart} cart */
  function cartDisplayLabel(cart) {
    if (cart.uid) {
      var uid = String(cart.uid).replace(/^0+/, '');
      if (!uid) uid = String(cart.uid);
      return 'CART-' + uid;
    }
    return 'CART-' + String(cart.id);
  }

  /** @param {string|null} iso */
  function formatCartDate(iso) {
    if (!iso || !String(iso).trim()) return '';
    try {
      var d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      var formatted = new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(d);
      return formatted.replace(/\b(am|pm)\b/gi, function (match) {
        return match.toUpperCase();
      });
    } catch {
      return '';
    }
  }

  /** @param {string|null} amount @param {string} currencyCode */
  function formatMoneyAmount(amount, currencyCode) {
    if (amount == null || String(amount).trim() === '') return '';
    var n = Number(String(amount).replace(/,/g, ''));
    if (!Number.isFinite(n)) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(n);
    } catch {
      return (currencyCode || 'USD') + ' ' + n.toFixed(2);
    }
  }

  /** @param {number|null} weight @param {string} unit */
  function formatWeight(weight, unit) {
    if (weight == null || !Number.isFinite(weight)) return '';
    return weight.toFixed(2) + ' ' + (unit || 'g');
  }

  /** @param {HTMLElement} modal */
  function getParts(modal) {
    return {
      title: modal.querySelector('[data-sync-order-cart-selector-title]'),
      subtitle: modal.querySelector('[data-sync-order-cart-selector-subtitle]'),
      banner: modal.querySelector('[data-sync-order-cart-selector-banner]'),
      status: modal.querySelector('[data-sync-order-cart-selector-status]'),
      sections: modal.querySelector('[data-sync-order-cart-selector-sections]'),
      refresh: modal.querySelector('[data-sync-order-cart-selector-refresh]'),
      syncBtn: modal.querySelector('[data-sync-order-cart-selector-sync]'),
      cancelButtons: modal.querySelectorAll('[data-sync-order-cart-selector-dismiss]'),
      dialog: modal.querySelector('[role="dialog"]'),
    };
  }

  /** @param {HTMLElement} modal @param {SyncDestination|null} value */
  function finishSelector(modal, value) {
    var parts = getParts(modal);
    if (parts.dialog && selectorState.tabHandler) {
      parts.dialog.removeEventListener('keydown', selectorState.tabHandler);
    }
    selectorState.tabHandler = null;
    if (selectorState.escHandler) {
      document.removeEventListener('keydown', selectorState.escHandler, true);
    }
    selectorState.escHandler = null;

    if (selectorState.abortController) {
      selectorState.abortController.abort();
      selectorState.abortController = null;
    }

    document.documentElement.style.overflow = selectorState.htmlOverflow;
    document.body.style.overflow = selectorState.bodyOverflow;
    document.body.classList.remove('sync-order-cart-selector-open');

    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');

    var fn = selectorState.resolve;
    var refocus = selectorState.lastFocus;
    selectorState.modal = null;
    selectorState.resolve = null;
    selectorState.customer = null;
    selectorState.selectedDestination = null;
    selectorState.activeCarts = [];
    selectorState.expiredCarts = [];
    selectorState.fetchState = 'idle';
    selectorState.options = null;
    selectorState.isSubmitting = false;

    if (fn) fn(value);

    if (refocus && typeof refocus.focus === 'function') {
      try {
        refocus.focus();
      } catch (_) {}
    }
  }

  /** @param {HTMLElement} modal */
  function updateSyncButton(modal) {
    var parts = getParts(modal);
    if (!(parts.syncBtn instanceof HTMLButtonElement)) return;
    var ready = selectorState.fetchState === 'ready';
    var hasDestination = !!selectorState.selectedDestination;
    parts.syncBtn.disabled = !ready || !hasDestination || selectorState.isSubmitting;
  }

  /** @param {HTMLElement} modal @param {string} message @param {'status'|'error'} [kind] */
  function setStatus(modal, message, kind) {
    var parts = getParts(modal);
    if (!(parts.status instanceof HTMLElement)) return;
    if (!message) {
      parts.status.hidden = true;
      parts.status.textContent = '';
      parts.status.classList.remove('sync-order-cart-selector__status--error');
      return;
    }
    parts.status.hidden = false;
    parts.status.textContent = message;
    parts.status.classList.toggle('sync-order-cart-selector__status--error', kind === 'error');
  }

  /** @param {HTMLElement} modal */
  function renderCustomerHeader(modal) {
    var parts = getParts(modal);
    var customer = selectorState.customer;
    if (!customer) return;

    var number = String(customer.customerNumber || '').trim();
    var name = String(customer.name || '').trim();
    var headline = number && name ? number + ' - ' + name : name || number || String(customer.id);

    if (parts.title instanceof HTMLElement) parts.title.textContent = headline;
    if (parts.subtitle instanceof HTMLElement) {
      if (number) {
        parts.subtitle.textContent = 'Customer No: ' + number;
        parts.subtitle.hidden = false;
      } else {
        parts.subtitle.hidden = true;
        parts.subtitle.textContent = '';
      }
    }
  }

  /** @param {HTMLElement} parent @param {string} tag @param {string} className @param {string} [text] */
  function el(parent, tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    parent.appendChild(node);
    return node;
  }

  /** @param {HTMLElement} modal @param {NormalizedCart} cart @param {'active'|'expired'} kind */
  function renderCartCard(modal, cart, kind) {
    var opts = selectorState.options || {};
    var currencyCode =
      cart.currency || (opts.currencyCode != null ? String(opts.currencyCode) : 'USD');

    var card = document.createElement('article');
    card.className =
      'sync-order-cart-selector__card sync-order-cart-selector__card--' +
      kind +
      (selectorState.selectedDestination &&
      selectorState.selectedDestination.type === 'EXISTING_CART' &&
      selectorState.selectedDestination.cartId === cart.id
        ? ' sync-order-cart-selector__card--selected'
        : '');

    var info = el(card, 'div', 'sync-order-cart-selector__card-info');
    var top = el(info, 'div', 'sync-order-cart-selector__card-top');
    el(top, 'span', 'sync-order-cart-selector__cart-id', cartDisplayLabel(cart));
    el(
      top,
      'span',
      'sync-order-cart-selector__status-pill sync-order-cart-selector__status-pill--' + kind,
      kind === 'active' ? 'ACTIVE' : 'EXPIRED'
    );

    var metaBits = [];
    if (kind === 'active') {
      var created = formatCartDate(cart.createdAt);
      if (created) metaBits.push('Created: ' + created);
    } else {
      var expired = formatCartDate(cart.expiresAt);
      if (expired) metaBits.push('Expired: ' + expired);
    }
    if (cart.salesRepName) metaBits.push('SR Name: ' + cart.salesRepName);
    if (metaBits.length) el(info, 'p', 'sync-order-cart-selector__meta', metaBits.join(' · '));

    var metrics = el(card, 'div', 'sync-order-cart-selector__metrics');
    var itemsMetric = el(metrics, 'div', 'sync-order-cart-selector__metric sync-order-cart-selector__metric--items');
    el(itemsMetric, 'span', 'sync-order-cart-selector__metric-label', 'ITEMS');
    el(itemsMetric, 'span', 'sync-order-cart-selector__metric-value', String(cart.totalItems));

    var weightMetric = el(metrics, 'div', 'sync-order-cart-selector__metric sync-order-cart-selector__metric--weight');
    el(weightMetric, 'span', 'sync-order-cart-selector__metric-label', 'TOTAL WEIGHT');
    var weightText = formatWeight(cart.totalWeight, cart.totalWeightUnit);
    el(weightMetric, 'span', 'sync-order-cart-selector__metric-value', weightText || '—');

    var totalMetric = el(metrics, 'div', 'sync-order-cart-selector__metric sync-order-cart-selector__metric--total');
    el(totalMetric, 'span', 'sync-order-cart-selector__metric-label', 'CART TOTAL');
    var totalText = formatMoneyAmount(cart.totalPrice, currencyCode);
    el(totalMetric, 'span', 'sync-order-cart-selector__metric-value', totalText || '—');

    if (kind === 'active') {
      var action = el(card, 'div', 'sync-order-cart-selector__card-action');
      var btn = el(action, 'button', 'sync-order-cart-selector__select-btn', 'Select');
      btn.type = 'button';
      var isSelected =
        selectorState.selectedDestination &&
        selectorState.selectedDestination.type === 'EXISTING_CART' &&
        selectorState.selectedDestination.cartId === cart.id;
      if (isSelected) {
        btn.textContent = 'Selected ✓';
        btn.classList.add('sync-order-cart-selector__select-btn--selected');
      }
      btn.disabled = selectorState.isSubmitting || selectorState.fetchState !== 'ready';
      btn.addEventListener('click', function () {
        if (selectorState.isSubmitting) return;
        var currentlySelected =
          selectorState.selectedDestination &&
          selectorState.selectedDestination.type === 'EXISTING_CART' &&
          selectorState.selectedDestination.cartId === cart.id;
        selectorState.selectedDestination = currentlySelected
          ? null
          : {
              type: 'EXISTING_CART',
              cartId: cart.id,
              cartLabel: cart.uid ? cartDisplayLabel(cart) : '',
            };
        renderSelector(modal);
      });
    }

    return card;
  }

  /** @param {HTMLElement} modal */
  function renderSelector(modal) {
    renderCustomerHeader(modal);
    var parts = getParts(modal);
    if (!(parts.sections instanceof HTMLElement)) return;

    parts.sections.innerHTML = '';
    parts.sections.hidden = selectorState.fetchState !== 'ready';

    if (parts.refresh instanceof HTMLButtonElement) {
      parts.refresh.hidden = selectorState.fetchState !== 'ready';
      parts.refresh.disabled = selectorState.fetchState !== 'ready' || selectorState.isSubmitting;
    }

    if (selectorState.fetchState === 'loading') {
      setStatus(modal, 'Loading carts...', 'status');
      updateSyncButton(modal);
      return;
    }

    if (selectorState.fetchState === 'error') {
      setStatus(modal, 'Unable to load carts. Please try again.', 'error');
      var retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.className = 'sync-order-cart-selector__retry';
      retryBtn.textContent = 'Retry';
      retryBtn.addEventListener('click', function () {
        fetchCustomerCarts(modal);
      });
      if (parts.status instanceof HTMLElement) parts.status.appendChild(retryBtn);
      updateSyncButton(modal);
      return;
    }

    setStatus(modal, '', 'status');

    var activeCarts = selectorState.activeCarts;
    var expiredCarts = selectorState.expiredCarts;

    if (activeCarts.length > 0) {
      var activeSection = el(parts.sections, 'section', 'sync-order-cart-selector__section');
      var activeHead = el(activeSection, 'div', 'sync-order-cart-selector__section-head');
      activeHead.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="9" cy="20" r="1"></circle><circle cx="17" cy="20" r="1"></circle><path d="M1 1h4l2.6 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"></path></svg>';
      el(activeHead, 'span', '', 'ACTIVE CARTS');
      el(
        activeHead,
        'span',
        'sync-order-cart-selector__count-badge sync-order-cart-selector__count-badge--active',
        String(activeCarts.length)
      );
      var activeCards = el(activeSection, 'div', 'sync-order-cart-selector__cards');
      activeCarts.forEach(function (cart) {
        activeCards.appendChild(renderCartCard(modal, cart, 'active'));
      });
    }

    if (expiredCarts.length > 0) {
      var expiredSection = el(parts.sections, 'section', 'sync-order-cart-selector__section');
      var expiredHead = el(expiredSection, 'div', 'sync-order-cart-selector__section-head');
      expiredHead.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="9" cy="20" r="1"></circle><circle cx="17" cy="20" r="1"></circle><path d="M1 1h4l2.6 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"></path></svg>';
      el(expiredHead, 'span', '', 'EXPIRED CARTS');
      el(
        expiredHead,
        'span',
        'sync-order-cart-selector__count-badge sync-order-cart-selector__count-badge--expired',
        String(expiredCarts.length)
      );

      var info = el(expiredSection, 'div', 'sync-order-cart-selector__info');
      info.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 8v5"></path><circle cx="12" cy="16.5" r=".6" fill="currentColor" stroke="none"></circle></svg>';
      el(
        info,
        'span',
        '',
        'Expired carts cannot receive new products. To use an expired cart, restore it first from the Sales App.'
      );

      var expiredCards = el(expiredSection, 'div', 'sync-order-cart-selector__cards');
      expiredCarts.forEach(function (cart) {
        expiredCards.appendChild(renderCartCard(modal, cart, 'expired'));
      });
    }

    if (activeCarts.length === 0 && expiredCarts.length === 0) {
      el(
        parts.sections,
        'p',
        'sync-order-cart-selector__empty',
        'No active carts found.'
      );
    }

    // Always available: active carts are an option, not a restriction.
    var createSection = el(parts.sections, 'section', 'sync-order-cart-selector__section');
    var createBtn = el(createSection, 'button', 'sync-order-cart-selector__create', '');
    createBtn.type = 'button';
    createBtn.disabled = selectorState.isSubmitting || selectorState.fetchState !== 'ready';
    el(createBtn, 'span', 'sync-order-cart-selector__create-icon', '+');
    var createLabel = el(createBtn, 'span', '', 'Create New Cart');
    if (
      selectorState.selectedDestination &&
      selectorState.selectedDestination.type === 'NEW_CART'
    ) {
      createBtn.classList.add('sync-order-cart-selector__create--selected');
      createLabel.textContent = 'Create New Cart (Selected ✓)';
    }
    createBtn.addEventListener('click', function () {
      if (selectorState.isSubmitting) return;
      // Mutual exclusivity: selecting NEW_CART clears any EXISTING_CART selection.
      selectorState.selectedDestination = { type: 'NEW_CART' };
      renderSelector(modal);
    });

    updateSyncButton(modal);
  }

  /** @param {Record<string, unknown>} data */
  function extractListPayload(data) {
    var unwrapped = unwrapApiData(data);
    if (unwrapped && typeof unwrapped === 'object') {
      var root = /** @type {Record<string, unknown>} */ (unwrapped);
      if (Array.isArray(root.items)) {
        return {
          items: root.items,
          pagination: root.pagination && typeof root.pagination === 'object' ? root.pagination : null,
        };
      }
    }
    if (Array.isArray(unwrapped)) {
      return { items: unwrapped, pagination: null };
    }
    return { items: [], pagination: null };
  }

  /** @param {unknown} pagination @param {number} page @param {number} limit */
  function hasMorePages(pagination, page, limit) {
    if (!pagination || typeof pagination !== 'object') return false;
    var pg = /** @type {Record<string, unknown>} */ (pagination);
    var totalPages = toFiniteNumber(pg.totalPages != null ? pg.totalPages : pg.total_pages);
    if (totalPages != null && totalPages > page) return true;
    var total = toFiniteNumber(pg.total != null ? pg.total : pg.totalItems != null ? pg.totalItems : pg.total_items);
    if (total != null && total > page * limit) return true;
    return false;
  }

  /** @param {HTMLElement} modal */
  async function fetchCustomerCarts(modal) {
    var opts = selectorState.options || {};
    var customer = selectorState.customer;
    if (!customer) return;

    if (selectorState.abortController) {
      selectorState.abortController.abort();
    }
    var abortController = new AbortController();
    selectorState.abortController = abortController;

    var fetchSequence = ++selectorState.fetchSequence;
    selectorState.fetchState = 'loading';
    selectorState.selectedDestination = null;
    selectorState.activeCarts = [];
    selectorState.expiredCarts = [];
    renderSelector(modal);

    var cartsPath = normalizeStorefrontPath(opts.cartsPath || '/apps/aone-api/carts');
    var salesRepEmail =
      opts.salesRepEmail != null ? String(opts.salesRepEmail).trim() : '';
    var customerId = Number(customer.id);
    var limit = 50;
    var page = 1;
    /** @type {NormalizedCart[]} */
    var allCarts = [];

    if (!salesRepEmail) {
      selectorState.fetchState = 'error';
      renderSelector(modal);
      return;
    }

    try {
      while (true) {
        if (abortController.signal.aborted) return;
        var params = new URLSearchParams({
          customerId: String(customerId),
          salesRepEmail: salesRepEmail,
          page: String(page),
          limit: String(limit),
        });
        var url = cartsPath + '?' + params.toString();

        if (typeof console !== 'undefined' && typeof console.debug === 'function') {
          console.debug('[SyncOrder] cart proxy request', { url: url, customerId: customerId });
        }

        var result = await fetchJson(url, { method: 'GET', signal: abortController.signal });

        if (typeof console !== 'undefined' && typeof console.debug === 'function') {
          console.debug('[SyncOrder] cart proxy response', {
            status: result.res.status,
            redirected: result.res.redirected,
            finalUrl: result.res.url,
          });
        }

        if (fetchSequence !== selectorState.fetchSequence) return;
        if (!result.res.ok) {
          selectorState.fetchState = 'error';
          renderSelector(modal);
          return;
        }

        var payload = extractListPayload(result.data);
        var rows = Array.isArray(payload.items) ? payload.items : [];
        rows.forEach(function (row) {
          var normalized = normalizeCartRow(row);
          if (normalized) allCarts.push(normalized);
        });

        if (!hasMorePages(payload.pagination, page, limit)) break;
        page += 1;
      }

      if (fetchSequence !== selectorState.fetchSequence) return;

      selectorState.activeCarts = allCarts.filter(function (cart) {
        return normalizeCartStatus(cart.status) === 'active';
      });
      selectorState.expiredCarts = allCarts.filter(function (cart) {
        return normalizeCartStatus(cart.status) === 'expired';
      });
      selectorState.fetchState = 'ready';
      renderSelector(modal);
    } catch (err) {
      if (abortController.signal.aborted || fetchSequence !== selectorState.fetchSequence) return;
      selectorState.fetchState = 'error';
      renderSelector(modal);
    }
  }

  /**
   * @param {{
   *   cartsPath?: string,
   *   salesRepEmail?: string,
   *   currencyCode?: string,
   *   customer: {id:number|string,name?:string,customerNumber?:string},
   *   cancelLabel?: string,
   *   syncOrderLabel?: string,
   *   bannerMessage?: string
   * }} options
   * @returns {Promise<SyncDestination|null>}
   */
  function openSyncCartSelector(options) {
    options = options || {};
    var modal = document.querySelector('[data-sync-order-cart-selector]');
    if (!(modal instanceof HTMLElement)) {
      return Promise.resolve(null);
    }

    var customerId = Number(options.customer && options.customer.id);
    if (!Number.isInteger(customerId) || customerId <= 0) {
      return Promise.resolve(null);
    }

    return new Promise(function (resolve) {
      var parts = getParts(modal);

      selectorState.modal = modal;
      selectorState.resolve = resolve;
      selectorState.customer = {
        id: customerId,
        name: options.customer && options.customer.name != null ? String(options.customer.name) : '',
        customerNumber:
          options.customer && options.customer.customerNumber != null
            ? String(options.customer.customerNumber)
            : '',
      };
      selectorState.selectedDestination = null;
      selectorState.activeCarts = [];
      selectorState.expiredCarts = [];
      selectorState.fetchState = 'loading';
      selectorState.options = /** @type {Record<string, unknown>} */ (options);
      selectorState.isSubmitting = false;
      selectorState.lastFocus = document.activeElement;
      selectorState.htmlOverflow = document.documentElement.style.overflow;
      selectorState.bodyOverflow = document.body.style.overflow;

      if (parts.syncBtn instanceof HTMLButtonElement && options.syncOrderLabel) {
        parts.syncBtn.textContent = String(options.syncOrderLabel);
      }
      parts.cancelButtons.forEach(function (btn) {
        if (btn.classList.contains('sync-order-cart-selector__cancel') && options.cancelLabel) {
          btn.textContent = String(options.cancelLabel);
        }
      });

      if (parts.banner instanceof HTMLElement) {
        if (options.bannerMessage) {
          parts.banner.hidden = false;
          parts.banner.textContent = String(options.bannerMessage);
        } else {
          parts.banner.hidden = true;
          parts.banner.textContent = '';
        }
      }

      modal.removeAttribute('hidden');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sync-order-cart-selector-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      renderSelector(modal);

      selectorState.escHandler = function (e) {
        if (e.key === 'Escape' && !selectorState.isSubmitting) finishSelector(modal, null);
      };
      document.addEventListener('keydown', selectorState.escHandler, true);

      if (parts.dialog instanceof HTMLElement) {
        selectorState.tabHandler = function (e) {
          if (e.key !== 'Tab') return;
          var list = focusableList(parts.dialog);
          if (!list.length) return;
          var first = list[0];
          var last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        };
        parts.dialog.addEventListener('keydown', selectorState.tabHandler);
      }

      modal.querySelectorAll('[data-sync-order-cart-selector-dismiss]').forEach(function (el) {
        el.addEventListener(
          'click',
          function () {
            if (selectorState.isSubmitting) return;
            finishSelector(modal, null);
          },
          { once: true }
        );
      });

      if (parts.refresh instanceof HTMLButtonElement) {
        parts.refresh.onclick = function () {
          if (selectorState.isSubmitting) return;
          fetchCustomerCarts(modal);
        };
      }

      if (parts.syncBtn instanceof HTMLButtonElement) {
        parts.syncBtn.addEventListener(
          'click',
          function () {
            if (selectorState.isSubmitting) return;
            if (!selectorState.selectedDestination) return;
            if (selectorState.fetchState !== 'ready') return;
            selectorState.isSubmitting = true;
            updateSyncButton(modal);
            renderSelector(modal);
            finishSelector(modal, selectorState.selectedDestination);
          },
          { once: true }
        );
      }

      fetchCustomerCarts(modal);
    });
  }

  win.AonegndSyncCartSelector = {
    open: openSyncCartSelector,
  };
})(typeof window !== 'undefined' ? window : globalThis);
